import * as React from 'react'
import { LabelledOutline } from '../LabelledOutline'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'

import * as styles from './MultipleCheckbox.module.scss'
import { useWatch } from 'react-hook-form'
import { tradeMarkSymbolParser } from 'util/titleParser'

export const MultipleCheckbox = ({
  name,
  description,
  label,
  options,
  register,
  saveAsJsonOnProfile,
  customStyle,
  onlyOneOptionPossible,
  setFieldValue,
  getValues,
  centerLabel = false,
  jsonFieldOnForm = 'extraFieldJson',
  noWrap = true,
  disabled,
  control,
}) => {
  // options can either take the form:
  //   [ 'option 1', 'option 2', ...]
  // or
  //   [ { label: "option 1", value: "ONE"}, { label: "option 2", value: "TWO"}]
  //
  // we start by normalizing options to look like the second form

  if (Array.isArray(options)) {
    if (options.length > 0) {
      if (typeof options[0] === 'string') {
        options = options.map(item => {
          return { label: item, value: item }
        })
      }
    }
  }

  const [selectedOptions, setSelectedOptions] = React.useState(
    Array.isArray(options) ? [] : '',
  )

  const watchValue = useWatch({
    control,
    name,
  })

  const previousDataSaved = getValues(jsonFieldOnForm)

  const handleClearSelectedOptions = React.useCallback(() => {
    setSelectedOptions([])
    if (saveAsJsonOnProfile) {
      const newJson = {
        ...previousDataSaved,
        [name]: [],
      }
      setFieldValue(jsonFieldOnForm, newJson)
    }
  }, [
    saveAsJsonOnProfile,
    previousDataSaved,
    name,
    setFieldValue,
    jsonFieldOnForm,
  ])

  React.useEffect(() => {
    if (
      watchValue &&
      Array.isArray(watchValue) &&
      watchValue.length === 0 &&
      selectedOptions.length > 0
    ) {
      handleClearSelectedOptions()
    }
  }, [handleClearSelectedOptions, selectedOptions.length, watchValue])

  const handleOptionChange = event => {
    const { value } = event.target
    if (onlyOneOptionPossible) {
      if (saveAsJsonOnProfile) {
        const newJson = {
          ...previousDataSaved,
          [name]: value,
        }
        setFieldValue(jsonFieldOnForm, newJson)
      }
      setFieldValue(name, [value])
      setSelectedOptions([value])
    } else {
      if (selectedOptions.includes(value)) {
        const newValue = selectedOptions.filter(option => option !== value)
        setSelectedOptions(newValue)
        setFieldValue(name, newValue)
        if (saveAsJsonOnProfile) {
          const newJson = {
            ...previousDataSaved,
            [name]: selectedOptions.filter(option => option !== value),
          }
          setFieldValue(jsonFieldOnForm, newJson)
        }
      } else {
        if (saveAsJsonOnProfile) {
          const newJson = {
            ...previousDataSaved,
            [name]: [...selectedOptions, value],
          }
          setFieldValue(jsonFieldOnForm, newJson)
        }
        setSelectedOptions([...selectedOptions, value])
        setFieldValue(name, [...selectedOptions, value])
      }
    }
  }

  return (
    <LabelledOutline
      customStyle={customStyle}
      centerLabel={centerLabel}
      noWrap={noWrap}
      label={label}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          rowGap: '14px',
        }}
      >
        {options.map(option => {
          return onlyOneOptionPossible ? (
            <FormControlLabel
              sx={{
                marginRight: '0',
              }}
              classes={{
                root: styles.labelCheckboxClass,
              }}
              slotProps={{
                typography: {
                  sx: {
                    fontSize: '0.75rem',
                  },
                },
              }}
              key={option.value}
              control={
                <Checkbox
                  {...register(name)}
                  size="medium"
                  classes={{
                    root: styles.checkboxClass,
                  }}
                  checked={selectedOptions[0] === option.value}
                  onChange={handleOptionChange}
                  value={option.value}
                  name={name}
                  disabled={disabled}
                />
              }
              label={option.label}
            />
          ) : (
            <FormControlLabel
              sx={{
                padding: '0',
              }}
              key={option}
              classes={{
                root: styles.labelCheckboxClass,
              }}
              slotProps={{
                typography: {
                  sx: {
                    fontSize: '0.75rem',
                  },
                },
              }}
              control={
                <Checkbox
                  {...register(name)}
                  size="medium"
                  classes={{
                    root: styles.checkboxClass,
                    checked: styles.checked,
                  }}
                  checked={selectedOptions.includes(option.value)}
                  onChange={handleOptionChange}
                  value={option.value}
                  name={name}
                  disabled={disabled}
                />
              }
              label={option.label}
            />
          )
        })}
      </div>
      {!!description && (
        <p
          className={styles.checkboxDescription}
          dangerouslySetInnerHTML={{
            __html: tradeMarkSymbolParser(description),
          }}
        />
      )}
    </LabelledOutline>
  )
}
